import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import IsoDatePicker from '../../schedule/components/IsoDatePicker'
import ItemPicker from '../components/ItemPicker'
import { emptyRfqForm, formFromRfq, rfqContractOptions, rfqFormProblem, rfqInput, type RfqForm, type RfqKind } from '../lib/rfq'
import type { CostTree } from '../lib/tree'
import type { CostContract, CostRfq } from '../types'

const NONE = 'none'
const KINDS: RfqKind[] = ['CONTRACT', 'CHANGE']
const KIND_KEY: Record<RfqKind, string> = { CONTRACT: 'CostControl_rfq_type_CONTRACT', CHANGE: 'CostControl_rfq_type_CHANGE' }
const NO_EXCLUDE: string[] = []

interface Props {
  open: boolean
  mode: 'create' | 'edit'
  rfq?: CostRfq | null
  contracts: CostContract[]
  tree: CostTree
  onOpenChange: (open: boolean) => void
  /** Resolves true when the server accepted the input; the dialog stays open on failure. */
  onSubmit: (input: Record<string, unknown>) => Promise<boolean>
}

/** Part D spec §8: title, the type and contract (create only), the budget item or group, the due date, the description. */
const RfqDialog: React.FC<Props> = ({ open, mode, rfq, contracts, tree, onOpenChange, onSubmit }) => {
  const { t } = useTranslation()
  const [form, setForm] = useState<RfqForm>(emptyRfqForm)
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setForm(rfq ? formFromRfq(rfq) : emptyRfqForm())
    setTouched(false)
    setSubmitting(false)
    // Only on opening: a background reload of the RFQ must not wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const set = <K extends keyof RfqForm>(key: K, value: RfqForm[K]) => setForm((f) => ({ ...f, [key]: value }))
  // Spec §3: after the award the RFQ keeps the item it was decided on.
  const nodeLocked = mode === 'edit' && rfq?.status !== 'OPEN'
  const node = form.nodeId ? tree.byId.get(form.nodeId)?.activity : undefined
  const problem = rfqFormProblem(form)
  const submit = async () => {
    setTouched(true)
    if (problem || submitting) return
    setSubmitting(true)
    const ok = await onSubmit(rfqInput(form, mode, rfq))
    setSubmitting(false)
    if (ok) onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t(mode === 'create' ? 'CostControl_rfq_new' : 'CostControl_rfq_edit')}</DialogTitle>
          {mode === 'create' && <DialogDescription>{t('CostControl_rfq_dialog_hint')}</DialogDescription>}
        </DialogHeader>
        <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); submit() }}>
          <div className="space-y-1.5">
            <Label htmlFor="cc-rfq-title">{t('CostControl_rfq_field_title')} *</Label>
            <Input id="cc-rfq-title" value={form.title} onChange={(e) => set('title', e.target.value)} autoFocus />
          </div>
          {mode === 'create' && (
            <div className="space-y-1.5">
              <Label>{t('CostControl_rfq_field_type')}</Label>
              <RadioGroup value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v as RfqKind, contractId: null }))} className="flex flex-wrap gap-x-6 gap-y-2">
                {KINDS.map((k) => (
                  <label key={k} className="flex cursor-pointer items-center gap-2 text-sm">
                    <RadioGroupItem value={k} />
                    {t(KIND_KEY[k])}
                  </label>
                ))}
              </RadioGroup>
            </div>
          )}
          {mode === 'create' && form.kind === 'CHANGE' && (
            <div className="space-y-1.5">
              <Label htmlFor="cc-rfq-contract">{t('CostControl_rfq_field_contract')} *</Label>
              <Select value={form.contractId ?? NONE} onValueChange={(v) => set('contractId', v === NONE ? null : v)}>
                <SelectTrigger id="cc-rfq-contract"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {!form.contractId && <SelectItem value={NONE}>{t('CostControl_rfq_pick_contract')}</SelectItem>}
                  {rfqContractOptions(contracts).map((c) => <SelectItem key={c.id} value={c.id}>{c.number ? `${c.number} · ${c.name}` : c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{t('CostControl_rfq_field_node')}</Label>
            <div className="flex items-center gap-2">
              <ItemPicker
                variant="select"
                withGroups
                tree={tree}
                excludeIds={NO_EXCLUDE}
                disabled={nodeLocked || submitting}
                label={node ? (node.code ? `${node.code} · ${node.name}` : node.name) : undefined}
                onPick={(nodeId) => set('nodeId', nodeId)}
              />
              {form.nodeId && !nodeLocked && (
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => set('nodeId', null)} aria-label={t('CostControl_rfq_node_clear')}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            {nodeLocked && <p className="text-xs text-muted-foreground">{t('CostControl_rfq_node_locked')}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-rfq-due">{t('CostControl_rfq_field_dueDate')}</Label>
            <IsoDatePicker id="cc-rfq-due" value={form.dueDate} onChange={(v) => set('dueDate', v)} clearable />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-rfq-description">{t('CostControl_rfq_field_description')}</Label>
            <Textarea id="cc-rfq-description" rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} />
          </div>
          {touched && problem && <p className="text-sm text-destructive">{t(problem)}</p>}
          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>{t('Common_cancel')}</Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t(mode === 'create' ? 'Common_create' : 'Common_save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default RfqDialog
